'use client'

import { Carousel, CarouselContent, CarouselNext, CarouselPrevious } from 'ui/carousel'
import AutoScroll from 'embla-carousel-auto-scroll'
import { IconNavigateBack, IconNavigateForward } from 'components/icons'
import { useCallback, useEffect, useRef, useState } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import { cn } from 'app/lib/utils'

type CarouselApi = ReturnType<typeof useEmblaCarousel>[1]

const CarouselSection = ({
    title,
    titleHighlight,
    autoScrollDirection = 'forward',
    children,
    hideArrow,
    titleClassName
}: ICarouselSectionProps) => {
    const [api, setApi] = useState<CarouselApi | undefined>()
    const containerRef = useRef<HTMLDivElement>(null)
    const [isInView, setIsInView] = useState(true)

    // Pause carousel auto-scroll when not in viewport to improve scroll performance
    useEffect(() => {
        const el = containerRef.current
        if (!el) return
        const observer = new IntersectionObserver(
            ([entry]) => setIsInView(!!entry?.isIntersecting),
            { rootMargin: '50px', threshold: 0 }
        )
        observer.observe(el)
        return () => observer.disconnect()
    }, [])

    useEffect(() => {
        if (!api) return
        try {
            const plugins = api.plugins()
            const pluginApi = plugins?.autoScroll as { stop?: () => void; play?: () => void } | undefined
            if (pluginApi?.stop && pluginApi?.play) {
                if (isInView) pluginApi.play()
                else pluginApi.stop()
            }
        } catch {
            /* ignore */
        }
    }, [api, isInView])

    const resetAutoScroll = useCallback(() => {
        if (api) {
            // Stop autoscroll
            try {
                const plugins = api.plugins()
                const pluginApi = plugins?.autoScroll
                if (pluginApi && typeof pluginApi === 'object' && 'stop' in pluginApi && 'play' in pluginApi) {
                    ;(pluginApi as any).stop()
                    // Resume after 5 seconds
                    setTimeout(() => (pluginApi as any).play(), 5000)
                }
            } catch (error) {
                // Silently handle plugin access errors
                console.warn('Error accessing autoScroll plugin:', error)
            }
        }
    }, [api])

    const scrollPrev = useCallback(() => {
        if (api) api.scrollPrev()
        resetAutoScroll()
    }, [api, resetAutoScroll])

    const scrollNext = useCallback(() => {
        if (api) api.scrollNext()
        resetAutoScroll()
    }, [api, resetAutoScroll])

    return (
        <div ref={containerRef} className='relative w-full mx-auto my-24 lg:my-32 overflow-hidden'>
            <Carousel
                setApi={setApi}
                plugins={[
                    AutoScroll({
                        speed: 1,
                        stopOnInteraction: true,
                        playOnInit: true,
                        direction: autoScrollDirection,
                        rootNode: emblaRoot => emblaRoot.parentElement as HTMLElement
                    })
                ]}
                className='w-full grid gap-y-4 pt-2'
                opts={{ align: 'start', loop: true, dragFree: true, containScroll: 'trimSnaps' }}
            >
                <div className='w-full flex justify-between items-center'>
                    <p className={cn('text-base md:text-xl lg:text-2xl font-bold', titleClassName)}>
                        {titleHighlight && <span className='disporty_text_gradient'>{titleHighlight} </span>}
                        {title}
                    </p>
                    {!hideArrow && (
                        <div className='relative right-12 my-4 flex items-center gap-2'>
                            <CarouselPrevious
                                onClick={scrollPrev}
                                className='h-7 w-7 md:h-10 md:w-10 ml-5 md:ml-0 flex border border-gray-300 p-1 rounded hover:!bg-[rgba(70,118,216)]/[.24] bg-transparent backdrop-blur-md'
                                icon={<IconNavigateBack />}
                            />
                            <CarouselNext
                                onClick={scrollNext}
                                className='h-7 w-7 md:h-10 md:w-10 flex border border-gray-300 p-1 rounded hover:!bg-[rgba(70,118,216)]/[.24] bg-transparent backdrop-blur-md'
                                icon={<IconNavigateForward />}
                            />
                        </div>
                    )}
                </div>
                <CarouselContent className='-ml-4'>{children}</CarouselContent>
            </Carousel>
        </div>
    )
}

export default CarouselSection
